import { Badge, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";

import BellIcon from "@/icons/BellIcon";
import ChatAltIcon from "@/icons/ChatAltIcon";
import { readNotices } from "@/api/notices";
import routes from "@/routes";

import useUnread from "../useUnread";
import { requestOpenMessenger } from "../useUnreadCommands";
import { formatCount } from "../formatCount";
import styles from "./unread-panel.module.css";

const latestText = (latest) => latest?.title || latest?.content || "";

/**
 * Плашка непрочитанного над подвалом меню: сообщения мессенджера
 * и уведомления с текстом последнего.
 * @param {{ collapsed: boolean, config: object }} props
 */
export default function UnreadPanel({ collapsed = false, config }) {
  const navigate = useNavigate();
  const { notices, messenger } = useUnread(config);

  const showMessenger = Boolean(config?.messenger_enabled);
  const noticesCount = notices.count ?? 0;
  const text = latestText(notices.latest);

  const openNotices = () => navigate(routes.notices("inbox"));

  const openLatest = (event) => {
    event.stopPropagation();
    const { latest } = notices;
    if (!latest) return;
    if (latest.uuid) {
      readNotices([latest.uuid]).catch(() => {});
    }
    navigate(latest.object_link || routes.notices("inbox"));
  };

  const messengerLabel = `Новых сообщений: ${formatCount(messenger.count)}`;
  const noticesLabel =
    noticesCount > 0
      ? `Уведомления: ${formatCount(noticesCount)}`
      : "Нет новых уведомлений";

  if (collapsed) {
    return (
      <div className={`${styles.panel} ${styles.compact}`}>
        {showMessenger && (
          <Tooltip placement="right" title={messengerLabel}>
            <button
              type="button"
              className={styles.iconButton}
              onClick={requestOpenMessenger}
              aria-label={messengerLabel}
            >
              <Badge count={messenger.count} size="small" overflowCount={99}>
                <ChatAltIcon className={styles.icon} />
              </Badge>
            </button>
          </Tooltip>
        )}
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      {showMessenger && (
        <button
          type="button"
          className={`${styles.row} ${styles.rowMessenger} ${
            messenger.count > 0 ? styles.rowActive : ""
          }`}
          onClick={requestOpenMessenger}
        >
          <ChatAltIcon className={styles.icon} />
          <span className={styles.label}>{messengerLabel}</span>
        </button>
      )}
      <div
        role="button"
        tabIndex={0}
        className={`${styles.row} ${styles.rowNotices} ${
          noticesCount > 0 ? styles.rowActive : ""
        }`}
        onClick={openNotices}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") openNotices();
        }}
      >
        <BellIcon className={styles.icon} />
        <span className={styles.body}>
          <span className={styles.label}>{noticesLabel}</span>
          {noticesCount > 0 && text && (
            <span
              role="link"
              tabIndex={0}
              className={styles.latest}
              title={text}
              onClick={openLatest}
              onKeyDown={(event) => {
                if (event.key === "Enter") openLatest(event);
              }}
            >
              {text}
            </span>
          )}
        </span>
      </div>
    </div>
  );
}
